import { Controller } from "@hotwired/stimulus"

// Public checkout — toggles shipping fields based on variant, updates recap.
// Values:
//   shipping-cents : flat shipping fee in millimes for physical variants
// Targets:
//   form            : the whole form (for context)
//   variantRadio    : each variant radio
//   variantLabel    : the <label> wrapping each variant radio
//   shippingBlock   : the address section (toggle display)
//   shippingLine    : recap line for shipping cost
//   shippingPrice   : recap shipping price text
//   recapName       : variant name in recap
//   recapPrice      : variant price in recap
//   total           : total in recap
export default class extends Controller {
  static targets = ["form", "variantRadio", "variantLabel", "shippingBlock", "shippingLine", "shippingPrice", "recapName", "recapPrice", "total", "discountLine", "discountPrice", "payButton", "preparationToken"]
  static values  = {
    shippingCents: Number,
    // Active promo info, server-rendered. Empty = no code applied.
    discountKind:  String,   // "percentage" | "fixed_amount" | ""
    discountValue: Number,   // percent (0-100) for %, or TND for fixed_amount
    // 29/10/2026 : où préparer la session Flouci avant le clic « Payer ».
    // Vide quand la carte n'est pas offerte sur ce livre.
    preparerUrl:   String
  }

  connect() {
    this.update()
    this.derniereCle = null
  }

  disconnect() {
    clearTimeout(this.preparationTimer)
  }

  selectVariant() {
    this.update()
    this.refreshLabelStyles()
    this.preparer()
  }

  // Au premier geste sur le formulaire (focus, saisie, changement d'édition
  // ou de moyen), la session Flouci se crée en arrière-plan : au clic, la
  // redirection vers la banque est immédiate si le montant n'a pas bougé.
  // Déclenché au geste, jamais à l'arrivée : 1 225 vues de livre pour 24
  // pages de checkout réellement engagées (60 jours au 29/10/2026).
  preparer() {
    if (!this.hasPreparerUrlValue || !this.preparerUrlValue) return
    if (!this.carteChoisie()) return
    const cle = this.cleMontant()
    if (cle === this.derniereCle) return
    this.derniereCle = cle
    clearTimeout(this.preparationTimer)
    this.preparationTimer = setTimeout(() => this.envoyerPreparation(cle), 400)
  }

  carteChoisie() {
    const radios = this.element.querySelectorAll('input[name="book_order[payment_method]"]')
    if (radios.length === 0) return true
    const choisi = [...radios].find((r) => r.checked)
    return !!choisi && choisi.value === "flouci"
  }

  cleMontant() {
    const variant = this.variantRadioTargets.find((r) => r.checked)?.value ||
      this.element.querySelector('input[name="book_order[book_variant_id]"]')?.value || ""
    const code = this.element.querySelector('input[name="book_order[discount_code]"]')?.value || ""
    return `${variant}|${code}`
  }

  async envoyerPreparation(cle) {
    const [variant, code] = cle.split("|")
    const corps = new FormData()
    corps.append("book_variant_id", variant)
    if (code) corps.append("discount_code", code)
    const email = this.element.querySelector('input[name="book_order[customer_email]"]')?.value
    if (email) corps.append("customer_email", email)
    const jeton = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const reponse = await fetch(this.preparerUrlValue, {
        method: "POST", body: corps, credentials: "same-origin",
        headers: { Accept: "application/json", ...(jeton ? { "X-CSRF-Token": jeton } : {}) }
      })
      if (!reponse.ok) throw new Error(`HTTP ${reponse.status}`)
      const donnees = await reponse.json()
      // Le montant a pu changer pendant l'appel : on ne garde que la dernière.
      if (cle === this.derniereCle && this.hasPreparationTokenTarget) {
        this.preparationTokenTarget.value = donnees.token || ""
      }
    } catch (_e) {
      // Sans préparation, le clic « Payer » suit le chemin d'avant : la
      // session se fait après la commande, derrière la page d'attente.
      if (this.hasPreparationTokenTarget) this.preparationTokenTarget.value = ""
      this.derniereCle = null
    }
  }

  refreshLabelStyles() {
    this.variantRadioTargets.forEach((radio) => {
      const label = radio.closest('[data-book-checkout-target="variantLabel"]')
      if (!label) return
      if (radio.checked) {
        label.style.border = "2px solid var(--brand)"
        label.style.background = "var(--brand-soft)"
      } else {
        label.style.border = "1px solid var(--line)"
        label.style.background = "var(--surface)"
      }
    })
  }

  update() {
    const checked = this.variantRadioTargets.find((r) => r.checked)
    if (!checked) return
    const requiresShipping = checked.dataset.requiresShipping === "1"
    const priceCents = parseInt(checked.dataset.priceCents || "0", 10)
    const shippingCents = requiresShipping ? this.shippingCentsValue : 0

    if (this.hasShippingBlockTarget) {
      this.shippingBlockTarget.style.display = requiresShipping ? "" : "none"
      // HTML5 valide les champs même cachés (required + pattern).
      // Quand on cache le bloc adresse, on disable les champs requis pour
      // que le navigateur ne bloque pas la soumission. On réactive sinon.
      // On stocke l'état "required" initial dans data-was-required pour ne
      // pas perdre l'info au toggle.
      this.shippingBlockTarget
        .querySelectorAll("input, select, textarea")
        .forEach((field) => {
          if (!field.dataset.bookCheckoutInitialized) {
            field.dataset.bookCheckoutInitialized = "1"
            field.dataset.wasRequired = field.required ? "1" : "0"
          }
          if (requiresShipping) {
            field.disabled = false
            if (field.dataset.wasRequired === "1") field.required = true
          } else {
            field.required = false
            field.disabled = true
          }
        })
    }
    if (this.hasShippingLineTarget) {
      this.shippingLineTarget.style.display = (requiresShipping && shippingCents > 0) ? "" : "none"
    }
    if (this.hasRecapNameTarget) {
      const label = checked.closest('label')
      const name = label ? label.querySelector('[style*="font-weight: 500"]')?.textContent : null
      if (name) this.recapNameTarget.textContent = name.trim()
    }
    if (this.hasRecapPriceTarget) {
      this.recapPriceTarget.textContent = this.formatTnd(priceCents)
    }
    if (this.hasShippingPriceTarget) {
      this.shippingPriceTarget.textContent = this.formatTnd(shippingCents)
    }

    // Promo: server-validated code reapplied client-side so the displayed
    // total stays consistent when the customer flips variants.
    const discountCents = this.computeDiscountMillimes(priceCents)
    if (this.hasDiscountPriceTarget) {
      this.discountPriceTarget.textContent = `−${this.formatTnd(discountCents)}`
    }

    if (this.hasTotalTarget) {
      const total = Math.max(priceCents + shippingCents - discountCents, 0)
      this.totalTarget.textContent = this.formatTnd(total)
    }
    // Le bouton final porte le même montant que le total.
    if (this.hasPayButtonTarget && !this.payButtonTarget.disabled) {
      const aPayer = Math.max(priceCents + shippingCents - discountCents, 0)
      this.payButtonTarget.value = aPayer === 0 ? "Valider la commande" : `Payer ${this.formatTnd(aPayer)}`
    }
  }

  computeDiscountMillimes(priceCents) {
    if (!this.discountKindValue) return 0
    if (this.discountKindValue === "percentage") {
      return Math.round(priceCents * this.discountValueValue / 100)
    }
    if (this.discountKindValue === "fixed_amount") {
      return Math.min(Math.round(this.discountValueValue * 1000), priceCents)
    }
    return 0
  }

  formatTnd(cents) {
    if (cents === 0) return "Gratuit"
    const value = cents / 1000
    const display = Number.isInteger(value) ? value : value.toFixed(cents % 10 === 0 ? 2 : 3)
    return `${display} TND`
  }
}
